import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, ShoppingBag, Plus, ExternalLink, Trash2, Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Creator dashboard for the Plyx Shop.
 *   /plyx/shop/manage
 *
 * Lists every product the creator has ever added (published or not),
 * lets them create new ones, toggle Plyx/AfriMarket visibility, and
 * unpublish from Plyx (the AfriMarket listing is preserved).
 */
const PlyxShopManage = () => {
  const { user, loading: authLoading } = usePlyxAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const reload = () => {
    setLoading(true);
    plyxApi
      .get("/plyx/shop/mine")
      .then((r) => setProducts(r.data?.products || []))
      .catch(() => toast.error("Could not load your shop"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!user) return;
    reload();
  }, [user]);

  if (!authLoading && !user) return <Navigate to="/plyx/login?next=/plyx/shop/manage" replace />;

  const togglePublishPlyx = async (p) => {
    try {
      await plyxApi.patch(`/plyx/shop/products/${p.listing_id}`, {
        published_on_plyx: !p.published_on_plyx,
      });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const togglePublishAfri = async (p) => {
    try {
      await plyxApi.patch(`/plyx/shop/products/${p.listing_id}`, {
        published_on_afrimarket: !p.published_on_afrimarket,
      });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const unpublish = async (p) => {
    if (!window.confirm(`Unpublish "${p.title}" from Plyx? (Will stay on AfriMarket if active there.)`)) return;
    try {
      await plyxApi.delete(`/plyx/shop/products/${p.listing_id}`);
      toast.success("Unpublished from Plyx");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unpublish failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-shop-manage-page">
      <div className="max-w-3xl mx-auto px-5 pt-6 pb-24">
        <Link to={user ? `/plyx/u/${user.handle}` : "/plyx"} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4"/> back to profile
        </Link>
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl tracking-tight">Your Plyx Shop</h1>
            <p className="text-xs text-white/50 mt-1">
              Add products buyers can shop while they watch your clips. Mirror to AfriMarket for cross-app reach.
            </p>
          </div>
          {user && (
            <Link
              to={`/plyx/u/${user.handle}/shop`}
              className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
              data-testid="plyx-shop-manage-view-public"
            >
              <Eye className="w-3 h-3"/> public view
            </Link>
          )}
        </div>

        {showForm ? (
          <NewProductForm
            onCancel={() => setShowForm(false)}
            onCreated={() => { setShowForm(false); reload(); }}
            creating={creating}
            setCreating={setCreating}
            linkedAfrimarket={Boolean(user?.linked_afrimarket_user_id)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            data-testid="plyx-shop-add-product"
            className="w-full mb-6 px-4 py-3 rounded-2xl border-2 border-dashed border-white/20 hover:border-[#FFB42D] text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4"/> Add new product
          </button>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="plyx-shop-manage-empty">
            <ShoppingBag className="w-10 h-10 mx-auto text-white/30 mb-3"/>
            <div className="font-semibold">No products yet</div>
            <div className="text-xs text-white/50 mt-1">Add your first product to start selling on Plyx.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((p) => (
              <div
                key={p.listing_id}
                data-testid={`plyx-shop-manage-row-${p.listing_id}`}
                className="rounded-xl border border-white/10 bg-white/5 p-3 flex items-center gap-3"
              >
                <div className="w-16 h-16 rounded-lg bg-black/50 overflow-hidden shrink-0">
                  {p.thumbnail_url ? (
                    <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20">
                      <ShoppingBag className="w-6 h-6"/>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{p.title}</div>
                  <div className="text-xs text-white/50 mt-0.5">
                    {p.currency === "GBP" ? "£" : p.currency === "USD" ? "$" : p.currency === "EUR" ? "€" : `${p.currency} `}
                    {p.price.toFixed(2)} · stock {p.stock}
                  </div>
                  <div className="flex gap-1.5 mt-1.5">
                    <button
                      type="button"
                      onClick={() => togglePublishPlyx(p)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 ${p.published_on_plyx ? "bg-[#FFB42D]/20 text-[#FFB42D]" : "bg-white/5 text-white/40"}`}
                      data-testid={`plyx-shop-toggle-plyx-${p.listing_id}`}
                    >
                      {p.published_on_plyx ? <Eye className="w-2.5 h-2.5"/> : <EyeOff className="w-2.5 h-2.5"/>} Plyx
                    </button>
                    <button
                      type="button"
                      onClick={() => togglePublishAfri(p)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 ${p.published_on_afrimarket ? "bg-[#00E0C7]/20 text-[#00E0C7]" : "bg-white/5 text-white/40"}`}
                      data-testid={`plyx-shop-toggle-afri-${p.listing_id}`}
                    >
                      {p.published_on_afrimarket ? <Eye className="w-2.5 h-2.5"/> : <EyeOff className="w-2.5 h-2.5"/>} AfriMarket
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={p.listing_url}
                    target="_top"
                    className="text-white/50 hover:text-white"
                    title="Open listing"
                    data-testid={`plyx-shop-open-${p.listing_id}`}
                  >
                    <ExternalLink className="w-4 h-4"/>
                  </a>
                  <button
                    type="button"
                    onClick={() => unpublish(p)}
                    className="text-red-400/70 hover:text-red-400"
                    title="Unpublish from Plyx"
                    data-testid={`plyx-shop-unpublish-${p.listing_id}`}
                  >
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const NewProductForm = ({ onCancel, onCreated, creating, setCreating, linkedAfrimarket }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [stock, setStock] = useState(1);
  const [imageUrl, setImageUrl] = useState("");
  // Default to ON only when the seller is actually linked to AfriMarket —
  // otherwise the server returns 400 "Link your AfriMarket account…".
  const [mirror, setMirror] = useState(Boolean(linkedAfrimarket));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    const priceN = parseFloat(price);
    if (!(priceN >= 0)) { toast.error("Enter a valid price"); return; }
    setCreating(true);
    try {
      await plyxApi.post("/plyx/shop/products", {
        title: title.trim(),
        description: description.trim() || null,
        price: priceN,
        currency,
        stock: Number(stock) || 1,
        images: imageUrl.trim() ? [imageUrl.trim()] : [],
        category: "Plyx Shop",
        mirror_to_afrimarket: mirror,
      });
      toast.success("Product published!");
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create product");
    } finally {
      setCreating(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3"
      data-testid="plyx-shop-new-form"
    >
      <h2 className="font-semibold text-sm uppercase tracking-wider text-[#FFB42D]">New product</h2>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Product title"
        maxLength={120}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
        data-testid="plyx-shop-new-title"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        maxLength={2400}
        className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
        data-testid="plyx-shop-new-description"
      />
      <div className="grid grid-cols-2 gap-3">
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="Price"
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
          data-testid="plyx-shop-new-price"
        />
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
          data-testid="plyx-shop-new-currency"
        >
          <option value="GBP">GBP £</option>
          <option value="USD">USD $</option>
          <option value="EUR">EUR €</option>
          <option value="CAD">CAD $</option>
          <option value="NGN">NGN ₦</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          type="number"
          min="0"
          step="1"
          placeholder="Stock"
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
          data-testid="plyx-shop-new-stock"
        />
        <input
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="Image URL"
          className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
          data-testid="plyx-shop-new-image-url"
        />
      </div>
      <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={mirror}
          onChange={(e) => setMirror(e.target.checked)}
          className="mt-0.5 accent-[#FFB42D]"
          data-testid="plyx-shop-new-mirror"
          disabled={!linkedAfrimarket}
        />
        <span>
          <ShieldCheck className="w-3 h-3 inline text-[#00E0C7] mr-1"/>
          Also list on AfriMarket
          {!linkedAfrimarket && (
            <span className="block text-[10px] text-white/40 mt-0.5">
              Link your AfriMarket account on the Wallet page to enable cross-app listing.
            </span>
          )}
        </span>
      </label>
      <div className="flex items-center gap-2 pt-2">
        <button
          type="submit"
          disabled={creating}
          data-testid="plyx-shop-new-submit"
          className="px-5 py-2 rounded-full bg-gradient-to-r from-[#FF2D7A] to-[#FFB42D] text-[#0A0A0A] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
          Publish
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="plyx-shop-new-cancel"
          className="px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm hover:bg-white/15"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default PlyxShopManage;
